import { useEffect, useMemo, useState } from 'react';
import { Account } from '../models/accounts';
import { useAccountsByIds } from './useAccountsByIds';
import { useInfinityAccountsQuery } from './useInfinityAccountsQuery';
import { Option } from 'src/common/components/Inputs/Autocomplete/utils/models';
type UseAccountAutocompleteProps = {
  initialId: string | null;
  search: string;
  hints: string[];
  onSelectedChange: (option: Option | null) => void;
};

const mapAccountToOption = (accounts: Account[]) => {
  return accounts.map((account) => {
    return {
      id: account.accountRef.toString(),
      name: account.accountName,
    };
  });
};

export const useAccountAutocomplete = ({
  search,
  initialId,
  hints,
  onSelectedChange,
}: UseAccountAutocompleteProps) => {
  const displayHints = !search;

  const {
    accounts: accountsData,
    isFetched,
    ...accountsQuery
  } = useInfinityAccountsQuery(search, {
    minTime: 250,
  });

  const {
    selectedAccounts: initialSelectedAccounts,
    isPending: isIntialFetching,
    isFetched: isIntialFetched,
  } = useAccountsByIds(initialId ? [initialId] : []);

  const [selectedOption, setSelectedOption] = useState<Option | null>(null);
  const shouldResetOptions = !initialId && !selectedOption;

  useEffect(() => {
    if (isIntialFetched) {
      setSelectedOption(mapAccountToOption(initialSelectedAccounts)[0] || null);
    }
  }, [initialSelectedAccounts, isIntialFetched]);

  const updateSelectedOption = (option: Option | null) => {
    setSelectedOption(option);
    onSelectedChange(option);
  };

  const { selectedAccounts: hintedAccounts, isFetched: isHintFetched } = useAccountsByIds(hints);
  const hintOptions = useMemo(
    () => (displayHints ? mapAccountToOption(hintedAccounts) : []),
    [hintedAccounts, displayHints],
  );

  const options = useMemo(() => {
    return mapAccountToOption(accountsData);
  }, [accountsData, search]);

  useEffect(() => {
    if (shouldResetOptions) {
      updateSelectedOption(null);
    }
  }, [shouldResetOptions]);

  return {
    selectedOption: selectedOption,
    isSelectedOptionFetching: isIntialFetching,
    hintOptions: hintOptions,
    updateSelectedOption,
    options: options,
    isFetched: isFetched && isHintFetched,
    ...accountsQuery,
  };
};
